import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { lovable } from "@/integrations/lovable"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Loader2, Moon, Sun, Mail, Lock, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { useTheme } from "next-themes"
import { BiscuitMini } from "@/components/biscuit/BiscuitMini"
import { haptic } from "@/lib/utils/haptic"

const DEMO_EMAIL = "demo@moneybees.app"
const DEMO_PASSWORD = "DemoUser2026!"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  )
}

type Mode = "signin" | "signup"

export default function AuthPage() {
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const [mode, setMode] = useState<Mode>("signin")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isDemoLoading, setIsDemoLoading] = useState(false)
  // null = unknown (still checking). Google is only offered once we know it's enabled.
  const [googleEnabled, setGoogleEnabled] = useState<boolean | null>(null)

  const anyLoading = isEmailLoading || isGoogleLoading || isDemoLoading

  // A returning user with a live session should never see the login form — the "/"
  // route forwards here, so bounce them straight to the dashboard.
  useEffect(() => {
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session) navigate("/home", { replace: true })
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) navigate("/home", { replace: true })
    })
    return () => { active = false; sub.subscription.unsubscribe() }
  }, [navigate])

  // Ask Supabase which providers are actually enabled so we never redirect the user
  // into a dead OAuth flow (a disabled provider returns a raw 400 JSON page).
  useEffect(() => {
    let active = true
    fetch(`${SUPABASE_URL}/auth/v1/settings`, { headers: { apikey: SUPABASE_KEY } })
      .then((r) => r.json())
      .then((s) => { if (active) setGoogleEnabled(Boolean(s?.external?.google)) })
      .catch(() => { if (active) setGoogleEnabled(false) })
    return () => { active = false }
  }, [])

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    if (anyLoading) return
    const cleanEmail = email.trim()
    if (!cleanEmail || !password) {
      toast.error("Enter your email and password 🐾")
      return
    }
    if (password.length < 6) {
      toast.error("Password needs at least 6 characters")
      return
    }
    setIsEmailLoading(true)
    haptic("tap")
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email: cleanEmail,
          password,
          options: { emailRedirectTo: `${window.location.origin}/home` },
        })
        if (error) throw error
        // With email auto-confirm on, signUp returns a session. If it doesn't,
        // confirmation is required.
        if (!data.session) {
          toast.success("Almost there — check your email to confirm 🐾")
          return
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password })
        if (error) throw error
      }
      haptic("success")
      toast.success(mode === "signup" ? "Welcome to Pawket! Biscuit's thrilled 🐾" : "Welcome back! 🐾")
      navigate("/home")
    } catch (err: any) {
      console.error("Email auth error:", err)
      haptic("error")
      const msg = err?.message || "That didn't work. Try again."
      toast.error(/invalid login credentials/i.test(msg) ? "Wrong email or password" : msg)
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (anyLoading) return
    if (googleEnabled === false) {
      toast.error("Google sign-in isn't set up yet — use your email below 🐾")
      return
    }
    setIsGoogleLoading(true)
    haptic("tap")
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/home`,
        extraParams: { prompt: "select_account" },
      })
      if (result.error) {
        console.error("Google sign-in error:", result.error)
        haptic("error")
        toast.error((result.error as any)?.message || "Google sign-in failed. Try again.")
        setIsGoogleLoading(false)
        return
      }
      if (result.redirected) {
        // Browser is redirecting to Google; keep loading state.
        return
      }
      // Tokens already set on supabase client — go home.
      haptic("success")
      toast.success("Welcome! 🐾")
      navigate("/home")
    } catch (err) {
      console.error("Unexpected Google sign-in error:", err)
      haptic("error")
      toast.error("Something went wrong. Try again.")
      setIsGoogleLoading(false)
    }
  }

  const handleDemoLogin = async () => {
    if (anyLoading) return
    setIsDemoLoading(true)
    haptic("tap")
    try {
      let { data, error } = await supabase.auth.signInWithPassword({
        email: DEMO_EMAIL,
        password: DEMO_PASSWORD,
      })

      if (error && /Invalid login credentials|Email not confirmed/i.test(error.message)) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: DEMO_EMAIL,
          password: DEMO_PASSWORD,
          options: {
            emailRedirectTo: `${window.location.origin}/home`,
            data: { first_name: "Demo", last_name: "User" },
          },
        })
        if (signUpError) {
          console.error("Demo signup error:", signUpError)
          haptic("error")
          toast.error(`Demo unavailable: ${signUpError.message}`)
          return
        }
        if (signUpData.session) {
          data = signUpData
          error = null
        } else {
          const retry = await supabase.auth.signInWithPassword({ email: DEMO_EMAIL, password: DEMO_PASSWORD })
          data = retry.data
          error = retry.error
        }
      }

      if (error) {
        console.error("Demo login error:", error)
        haptic("error")
        toast.error(error.message)
        return
      }

      haptic("success")
      toast.success("Welcome! Biscuit's been waiting 🐾")
      navigate("/home")
    } catch (err) {
      console.error("Unexpected demo login error:", err)
      haptic("error")
      toast.error("Demo login failed")
    } finally {
      setIsDemoLoading(false)
    }
  }

  return (
    <div className="relative min-h-dvh overflow-hidden bg-background flex flex-col">
      {/* Warm atmospheric wash — tokens shift with light/dark */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 520px at 50% -8%, hsl(var(--honey) / 0.20), transparent 60%)," +
            "radial-gradient(620px 480px at 112% 6%, hsl(var(--accent) / 0.16), transparent 55%)," +
            "radial-gradient(720px 560px at -12% 108%, hsl(var(--primary) / 0.12), transparent 55%)",
        }}
      />
      {/* Soft blurred blobs for organic depth */}
      <div className="absolute -top-24 -right-20 h-64 w-64 rounded-full bg-[hsl(var(--honey)/0.22)] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-28 -left-24 h-72 w-72 rounded-full bg-[hsl(var(--primary)/0.16)] blur-3xl pointer-events-none" />
      {/* Fine dotted texture */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      {/* Theme Toggle — anchored below the iOS notch / status bar */}
      <Button
        variant="ghost"
        size="icon"
        aria-pressed={theme === "dark"}
        className="fixed right-4 rounded-full active:scale-95 transition-transform z-10 h-11 w-11"
        style={{ top: "max(1rem, env(safe-area-inset-top))" }}
        onClick={() => { haptic("tap"); setTheme(theme === "dark" ? "light" : "dark") }}
      >
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        <span className="sr-only">Toggle theme</span>
      </Button>

      <main className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-sm mx-auto px-6 py-10">
        {/* Hero — Biscuit the corgi is the brand mark */}
        <div className="text-center mb-7 animate-fade-in-up" style={{ animationFillMode: "both" }}>
          <div className="mb-5 inline-block">
            <div className="relative">
              <div className="absolute inset-0 -m-3 rounded-full bg-[hsl(var(--honey)/0.35)] blur-2xl animate-pulse" />
              <div className="relative h-28 w-28 mx-auto flex items-center justify-center rounded-full bg-card/70 backdrop-blur-sm ring-1 ring-[hsl(var(--honey)/0.4)] shadow-[0_14px_45px_-12px_hsl(var(--honey)/0.55)]">
                <div className="scale-[1.55]"><BiscuitMini mood="happy" /></div>
              </div>
            </div>
          </div>
          <h1 className="font-display text-5xl font-semibold tracking-tight leading-none">Pawket</h1>
          <p className="text-muted-foreground mt-3 text-[15px]">
            {mode === "signup" ? "Create your account — Biscuit's waiting." : "Track your spend, treat your pup."}
          </p>
        </div>

        {/* Auth card */}
        <div
          className="rounded-3xl border border-border/60 bg-card/70 backdrop-blur-xl shadow-[0_24px_70px_-30px_hsl(var(--primary)/0.4)] p-5 animate-fade-in-up"
          style={{ animationDelay: "100ms", animationFillMode: "both" }}
        >
        {/* Email / password */}
        <form onSubmit={handleEmailAuth} className="space-y-3" noValidate>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={anyLoading}
              className="h-14 pl-11 rounded-2xl text-base bg-background/70"
              aria-label="Email"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
            <Input
              type={showPassword ? "text" : "password"}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={anyLoading}
              className="h-14 pl-11 pr-12 rounded-2xl text-base bg-background/70"
              aria-label="Password"
            />
            <button
              type="button"
              onClick={() => { haptic("tap"); setShowPassword((v) => !v) }}
              disabled={anyLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground active:scale-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--honey))]"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <Button
            type="submit"
            disabled={anyLoading}
            className="w-full h-14 text-base font-semibold rounded-2xl shadow-[0_12px_30px_-10px_hsl(var(--primary)/0.55)] hover:shadow-[0_16px_40px_-12px_hsl(var(--primary)/0.65)] active:scale-[0.98] transition-all"
          >
            {isEmailLoading ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {mode === "signup" ? "Creating account…" : "Signing in…"}</>
            ) : (
              mode === "signup" ? "Create account" : "Sign in"
            )}
          </Button>
        </form>

        <p className="text-sm text-center text-muted-foreground mt-3">
          {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
          <button
            type="button"
            onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
            disabled={anyLoading}
            className="font-semibold text-primary hover:underline px-2 py-1.5 -mx-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--honey))]"
          >
            {mode === "signup" ? "Sign in" : "Create one"}
          </button>
        </p>

        {/* Divider + alternative sign-ins. Google only renders once we confirm it's enabled. */}
        <div className="my-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-border/60" />
          <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">or</span>
          <span className="h-px flex-1 bg-border/60" />
        </div>

        <div className="space-y-3">
          {googleEnabled === true && (
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={anyLoading}
              className="w-full h-14 text-base font-semibold rounded-2xl bg-card hover:bg-card/80 border-2 shadow-sm hover:shadow-md active:scale-[0.98] transition-all"
            >
              {isGoogleLoading ? (
                <><Loader2 className="mr-3 h-5 w-5 animate-spin" /> Opening Google…</>
              ) : (
                <><GoogleLogo className="mr-3 h-5 w-5" /> Continue with Google</>
              )}
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={handleDemoLogin}
            disabled={anyLoading}
            data-testid="demo-login-button"
            className="w-full h-12 rounded-2xl text-base font-medium active:scale-[0.98] transition-all"
          >
            {isDemoLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Biscuit's prepping the treat jar…</>
            ) : (
              "Just exploring? Try the demo →"
            )}
          </Button>
        </div>

        </div>

        {/* Footer */}
        <p
          className="text-center text-xs text-muted-foreground mt-7 px-4 animate-fade-in-up"
          style={{ animationDelay: "180ms", animationFillMode: "both" }}
        >
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </main>
    </div>
  )
}
